/**
 * Billing Service - Manages user subscription tiers and quotas
 * 
 * ✅ Email is now UNIQUE identifier (one email = one user)
 * Simplified logic: uses email as primary lookup key
 * 
 * CACHE STRATEGY:
 * - Subscription status cached for 5 minutes (very fresh)
 * - Invalidated on: payment callback, manual upgrade, subscription change
 * - Uses CacheService for intelligent TTL management
 */

import { supabase } from '@/config/supabase';
import { CacheService } from './cacheService';

export interface UserSubscription {
  userId?: string; // Supabase UUID
  isPro: boolean;
  proExpiresAt: string | null;
  roadmapCount: number;
  hasPendingRequest?: boolean;
}

export const SUBSCRIPTION_LIMITS = {
  FREE: {
    MAX_ROADMAPS_PER_MONTH: 3, // Generous trial quota
  },
  PRO: {
    MAX_ROADMAPS_PER_MONTH: 999,
  }
};

export class BillingService {
  private static readonly SUBSCRIPTION_CACHE_KEY = 'billing_subscription';

  /**
   * Get current subscription status and quota usage (with intelligent caching)
   * Uses email as unique identifier (one email = one user)
   * 
   * @param userEmail Unique user email address
   */
  static async getSubscriptionStatus(userEmail: string): Promise<UserSubscription> {
    try {
      // Check cache first (5-minute TTL)
      const cacheKey = `${this.SUBSCRIPTION_CACHE_KEY}:${userEmail}`;
      const cached = CacheService.get(cacheKey);
      if (cached) {
        console.log('✅ Subscription status from cache (5-min TTL)');
        return cached as UserSubscription;
      }

      // Query by email (unique identifier)
      const [profileResponse, requestResponse] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('auth_user_id, is_pro, pro_expires_at, roadmap_generation_count, email')
          .eq('email', userEmail)
          .maybeSingle(),
        supabase
          .from('subscription_requests')
          .select('id')
          .eq('user_email', userEmail)
          .eq('status', 'pending')
          .limit(1)
      ]);

      if (profileResponse.error) throw profileResponse.error;

      // If user_profiles record doesn't exist, return default (free) subscription
      if (!profileResponse.data) {
        console.log('ℹ️ No user_profiles record found for', userEmail, '- returning default (free) subscription');
        const result: UserSubscription = {
          userId: undefined,
          isPro: false,
          proExpiresAt: null,
          roadmapCount: 0,
          hasPendingRequest: false
        };
        CacheService.set(cacheKey, result, undefined, 'subscription');
        return result;
      }

      const data = profileResponse.data;
      const now = new Date();
      const isPro = data.is_pro && (!data.pro_expires_at || new Date(data.pro_expires_at) > now);
      const hasPendingRequest = (requestResponse.data?.length ?? 0) > 0;

      const result: UserSubscription = {
        userId: data.auth_user_id,
        isPro,
        proExpiresAt: data.pro_expires_at,
        roadmapCount: data.roadmap_generation_count || 0,
        hasPendingRequest
      };

      // Cache for 5 minutes with 'subscription' category
      CacheService.set(cacheKey, result, undefined, 'subscription');
      console.log('✅ Subscription status cached (5-min TTL)');

      return result;
    } catch (error) {
      console.error('❌ Error fetching subscription status:', error);
      return { isPro: false, proExpiresAt: null, roadmapCount: 0, hasPendingRequest: false };
    }
  }

  /**
   * Check if user can generate a new roadmap
   * @param userEmail Unique user email address
   * @param intensity Roadmap intensity level
   */
  static async canGenerateRoadmap(userEmail: string, intensity: string): Promise<{ allowed: boolean; reason?: string }> {
    const status = await this.getSubscriptionStatus(userEmail);
    
    if (status.isPro) return { allowed: true };

    // Check intensity access
    // Only Beginner is free. Intermediate and Expert require Pro subscription.
    const intensityLower = intensity.toLowerCase();
    if ((intensityLower === 'expert' || intensityLower === 'intermediate') && !status.isPro) {
      return { 
        allowed: false, 
        reason: `${intensity} level roadmaps are a Pro feature. Upgrade to unlock professional learning paths!` 
      };
    }

    // Check monthly quota for free users (Beginner only)
    if (status.roadmapCount >= SUBSCRIPTION_LIMITS.FREE.MAX_ROADMAPS_PER_MONTH) {
      return { 
        allowed: false, 
        reason: `Monthly limit reached (${SUBSCRIPTION_LIMITS.FREE.MAX_ROADMAPS_PER_MONTH} beginner roadmaps). Upgrade to Pro for unlimited generation!` 
      };
    }

    return { allowed: true };
  }

  /**
   * Increment roadmap generation count
   * @param userEmail Unique user email address
   */
  static async recordGeneration(userEmail: string): Promise<void> {
    try {
      // Get current count
      const { data } = await supabase
        .from('user_profiles')
        .select('roadmap_generation_count')
        .eq('email', userEmail)
        .single();
      
      // Increment and update
      await supabase
        .from('user_profiles')
        .update({ 
          roadmap_generation_count: (data?.roadmap_generation_count || 0) + 1,
          last_generation_at: new Date().toISOString()
        })
        .eq('email', userEmail);

      // Invalidate subscription cache for this user
      this.invalidateCache(userEmail);
      console.log('✅ Generation recorded and cache invalidated');
    } catch (error) {
      console.error('❌ Error recording generation:', error);
    }
  }

  /**
   * Submit a manual upgrade request to be handled by admin
   * @param email User email (for subscription_requests table)
   * @param planType 'monthly' or 'yearly'
   * @param trxId Optional transaction ID
   */
  static async submitUpgradeRequest(email: string, planType: 'monthly' | 'yearly', trxId?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const amount = planType === 'monthly' ? 66 : 500;
      
      const { error } = await supabase
        .from('subscription_requests')
        .insert({
          user_email: email,
          plan_type: planType,
          amount,
          transaction_id: trxId,
          status: 'pending'
        });

      if (error) throw error;

      // Invalidate cache for this user
      this.invalidateCache(email);
      console.log('✅ Upgrade request submitted and cache invalidated');

      return { success: true };
    } catch (error: any) {
      console.error('❌ Error submitting upgrade request:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Initiate SSLCommerz payment flow
   * @param email User email (for payment tracking)
   * @param name User name (optional)
   */
  static async initiatePayment(email: string, name?: string): Promise<void> {
    try {
      const response = await fetch('/api/billing/initiate-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          name,
          amount: 500 // 500 BDT for Pro
        }),
      });

      let data;
      const text = await response.text();
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new Error(`Invalid response from server (${response.status})`);
      }

      if (response.ok && data.success && data.gatewayUrl) {
        // Redirect to SSLCommerz hosted page
        window.location.href = data.gatewayUrl;
      } else {
        throw new Error(data.error || `Payment initiation failed (${response.status})`);
      }
    } catch (error: any) {
      console.error('❌ Payment initiation error:', error);
      alert(`Payment Error: ${error.message}`);
    }
  }

  /**
   * Handle payment callback (update subscription)
   * Called by backend after successful payment
   * 
   * @param email User email
   * @param planType 'monthly' or 'yearly'
   */
  static async handlePaymentCallback(email: string, planType: 'monthly' | 'yearly'): Promise<void> {
    try {
      // Calculate pro expiration date
      const now = new Date();
      const expiresAt = planType === 'monthly' 
        ? new Date(now.setMonth(now.getMonth() + 1))
        : new Date(now.setFullYear(now.getFullYear() + 1));

      // Update subscription in database
      const { error } = await supabase
        .from('user_profiles')
        .update({
          is_pro: true,
          pro_expires_at: expiresAt.toISOString()
        })
        .eq('email', email);

      if (error) throw error;

      // Invalidate cache to show updated status immediately
      this.invalidateCache(email);
      console.log('✅ Payment processed, subscription updated, cache invalidated');
    } catch (error) {
      console.error('❌ Error handling payment callback:', error);
      throw error;
    }
  }

  /**
   * Invalidate subscription cache for a user
   * Called after subscription changes, payments, or generation records
   * 
   * @param userEmail User email address
   */
  private static invalidateCache(userEmail: string): void {
    const cacheKey = `${this.SUBSCRIPTION_CACHE_KEY}:${userEmail}`;
    CacheService.invalidateCategory('subscription');
    console.log(`🔄 Subscription cache invalidated for ${userEmail}`);
  }

  /**
   * Reset monthly roadmap count (called by admin or scheduler)
   * @param userEmail User email address
   */
  static async resetMonthlyCount(userEmail: string): Promise<void> {
    try {
      await supabase
        .from('user_profiles')
        .update({ roadmap_generation_count: 0 })
        .eq('email', userEmail);

      this.invalidateCache(userEmail);
      console.log('✅ Monthly count reset and cache invalidated');
    } catch (error) {
      console.error('❌ Error resetting monthly count:', error);
    }
  }

  /**
   * Manual upgrade endpoint (admin panel)
   * @param email User email
   * @param planType 'monthly' or 'yearly'
   */
  static async manualUpgrade(email: string, planType: 'monthly' | 'yearly'): Promise<void> {
    try {
      const now = new Date();
      const expiresAt = planType === 'monthly' 
        ? new Date(now.setMonth(now.getMonth() + 1))
        : new Date(now.setFullYear(now.getFullYear() + 1));

      const { error } = await supabase
        .from('user_profiles')
        .update({
          is_pro: true,
          pro_expires_at: expiresAt.toISOString()
        })
        .eq('email', email);

      if (error) throw error;

      // Clear the pending request
      await supabase
        .from('subscription_requests')
        .update({ status: 'approved' })
        .eq('user_email', email)
        .eq('status', 'pending');

      this.invalidateCache(email);
      console.log('✅ Manual upgrade applied and cache invalidated');
    } catch (error) {
      console.error('❌ Error applying manual upgrade:', error);
      throw error;
    }
  }
}
